// The Models page's Procore / ACC bar (owner 2026-10-25: "show the procore and the ACC
// connection on the header like shop drawings"). The shop drawings bar's look
// (ShopDrawingExternalDelivery), for models: per destination its logo, whether the account
// is connected, the account the project downloads and uploads as, and the models folder
// that "Get latest models" reads and "Send to" writes. Nothing to tick here - models are
// fetched and sent on demand, from the toolbar.
import React from "react";
import { Tooltip } from "antd";
import { AutodeskLogo, ProcoreLogo } from "./ProviderLogos";
import { connectProcore } from "./ProcoreConnectionPanel";
import { ProviderAccounts, destinationConnected } from "./AccountsCount";
import { openFolderPaths } from "./FolderPathsPanel";
import "./integrations.scss";

const PROVIDERS = [
  { key: "acc", name: "ACC", Logo: AutodeskLogo },
  { key: "procore", name: "Procore", Logo: ProcoreLogo },
];

// `data`: getProjectIntegrations(projectId) - { acc, procore, destinations? }
export default function ModelsConnectionBar({ data, projectId }) {
  // nothing until the server has answered for both providers
  if (!data || !data.acc || !data.procore) return null;

  // every destination (several per provider since 2026-10-24); none listed -> one per provider
  const entries = [];
  for (const { key, name, Logo } of PROVIDERS) {
    const base = { ...(data[key] || {}), key };
    const list = (data.destinations || []).filter((d) => d.provider === key);
    // CONNECTED IS THE DESTINATION'S OWN ACCOUNT (owner 2026-10-25: "make sure the acc
    // connected and procore connected on any projects"), not only the viewer's first one
    if (!list.length) entries.push({ id: key, name, Logo, p: { ...base, connected: destinationConnected(data, key, null) } });
    list.forEach((d) => entries.push({
      id: d.destination || key, Logo, name: d.name || name,
      p: { ...base, ...d, configured: base.configured, connected: destinationConnected(data, key, d), key },
    }));
  }

  return (
    <div className="sd-external-delivery models-connection-bar" data-testid="models-connection-bar">
      <b>Models from and to:</b>
      <ProviderAccounts data={data} />
      {entries.map(({ id, name, Logo, p }) => {
        const folder = p.targets && p.targets.models;
        // the MODELS DOWNLOAD and UPLOAD folders (owner 2026-10-25), the project's or the company's
        const fpaths = p.folderPaths || null;
        const off = p.key === "acc" && !p.configured;
        return (
          <span key={id} className="sd-external-provider sd-external-procore" data-testid={`models-connection-${id}`}>
            <span className="d-inline-flex align-items-center" style={{ gap: 4 }}>
              <Logo connected={!!p.connected} size={16} /> {name}
            </span>
            {off ? (
              <span className="text-muted">not set up on this server</span>
            ) : p.connected ? (
              <span className="text-success">Connected</span>
            ) : p.key === "procore" ? (
              <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={connectProcore} title="Connect your Procore account">
                Not connected
              </button>
            ) : (
              <Tooltip title="Connect your company's Autodesk account in My Projects">
                <span className="text-danger">Not connected</span>
              </Tooltip>
            )}
            {p.actor ? (
              <span className="sd-external-path" title={`The ${name} account this project downloads and uploads models with`}>
                as {p.actor.providerEmail || p.actor.userName}
                {!p.actor.connected ? <span className="text-danger"> (not working)</span> : null}
              </span>
            ) : null}
            {fpaths ? (
              <>
                <span className="sd-external-path" title="Get latest models reads .rvt / .nwc / .nwd from here" data-testid={`models-download-${id}`}>
                  Download: {fpaths.modelsDownload || <i>not set</i>}
                </span>
                <span className="sd-external-path" title="Send to and automatic uploads write here" data-testid={`models-upload-${id}`}>
                  Upload: {fpaths.modelsUpload || <i>not set</i>}
                </span>
              </>
            ) : (
              <span className="sd-external-path" title={folder && folder.set ? folder.path : `Choose the ${name} models folder: the settings icon beside the ${name} logo on the project card`}>
                Models: {folder && folder.set ? folder.path : <i>not set</i>}
              </span>
            )}
            {projectId && fpaths && (!fpaths.modelsDownload || !fpaths.modelsUpload) ? (
              <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={() => openFolderPaths(projectId)} data-testid={`models-set-folders-${id}`}>
                Set folders
              </button>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
